import React from 'react';
import {
  AlertTriangle,
  Trash2,
  Ban,
  Eraser,
  CheckCircle2,
  X,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';

export type ConfirmationType =
  | 'delete_conversation'
  | 'clear_chat'
  | 'block_user'
  | 'unblock_user'
  | 'edit_story'
  | 'delete_story'
  | 'custom';

interface ConfirmationModalProps {
  isOpen: boolean;
  type?: ConfirmationType;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  icon?: React.ReactNode;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  type = 'custom',
  title,
  description,
  confirmText,
  cancelText = 'إلغاء',
  isDestructive = true,
  isLoading = false,
  onConfirm,
  onCancel,
  icon,
}) => {
  if (!isOpen) return null;

  // Pick default icon and styling based on confirmation type
  const getIconAndStyle = () => {
    if (icon) {
      return {
        iconNode: icon,
        ringColor: isDestructive ? 'ring-rose-500/30' : 'ring-emerald-500/30',
        bgIcon: isDestructive ? 'bg-rose-500/15 text-rose-400' : 'bg-emerald-500/15 text-emerald-400',
        btnClass: isDestructive
          ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white shadow-lg shadow-rose-950/50'
          : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg shadow-emerald-950/50',
      };
    }

    switch (type) {
      case 'delete_conversation':
        return {
          iconNode: <Trash2 className="w-7 h-7" />,
          ringColor: 'ring-rose-500/40',
          bgIcon: 'bg-rose-500/20 text-rose-400',
          btnClass: 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white shadow-lg shadow-rose-950/50',
        };
      case 'clear_chat':
        return {
          iconNode: <Eraser className="w-7 h-7" />,
          ringColor: 'ring-amber-500/40',
          bgIcon: 'bg-amber-500/20 text-amber-400',
          btnClass: 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white shadow-lg shadow-amber-950/50',
        };
      case 'block_user':
        return {
          iconNode: <Ban className="w-7 h-7" />,
          ringColor: 'ring-rose-500/40',
          bgIcon: 'bg-rose-500/20 text-rose-400',
          btnClass: 'bg-gradient-to-r from-rose-700 to-red-700 hover:from-rose-600 hover:to-red-600 text-white shadow-lg shadow-rose-950/50',
        };
      case 'unblock_user':
        return {
          iconNode: <ShieldAlert className="w-7 h-7" />,
          ringColor: 'ring-teal-500/40',
          bgIcon: 'bg-teal-500/20 text-teal-400',
          btnClass: 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white shadow-lg shadow-teal-950/50',
        };
      case 'edit_story':
        return {
          iconNode: <CheckCircle2 className="w-7 h-7" />,
          ringColor: 'ring-emerald-500/40',
          bgIcon: 'bg-emerald-500/20 text-emerald-400',
          btnClass: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg shadow-emerald-950/50',
        };
      case 'delete_story':
        return {
          iconNode: <Trash2 className="w-7 h-7" />,
          ringColor: 'ring-rose-500/40',
          bgIcon: 'bg-rose-500/20 text-rose-400',
          btnClass: 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white shadow-lg shadow-rose-950/50',
        };
      default:
        return {
          iconNode: isDestructive ? <AlertTriangle className="w-7 h-7" /> : <Sparkles className="w-7 h-7" />,
          ringColor: isDestructive ? 'ring-rose-500/30' : 'ring-emerald-500/30',
          bgIcon: isDestructive ? 'bg-rose-500/15 text-rose-400' : 'bg-emerald-500/15 text-emerald-400',
          btnClass: isDestructive
            ? 'bg-gradient-to-r from-rose-600 to-red-600 text-white'
            : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white',
        };
    }
  };

  const styleMeta = getIconAndStyle();
  const resolvedConfirmText =
    confirmText ||
    (type === 'delete_conversation'
      ? 'نعم، احذف المحادثة'
      : type === 'clear_chat'
      ? 'نعم، امسح الرسائل'
      : type === 'block_user'
      ? 'نعم، احظر المستخدم'
      : type === 'unblock_user'
      ? 'إلغاء الحظر'
      : type === 'edit_story'
      ? 'تأكيد حفظ التعديل'
      : 'تأكيد الإجراء');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl font-['Cairo'] animate-in fade-in duration-200"
      onClick={onCancel}
    >
      <div
        className="relative w-full max-w-sm bg-[#0e1422] border border-slate-800 rounded-3xl p-6 shadow-2xl text-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow ambient background */}
        <div
          className={`absolute -top-16 -right-16 w-36 h-36 rounded-full blur-3xl pointer-events-none opacity-20 ${
            isDestructive ? 'bg-rose-500' : 'bg-emerald-500'
          }`}
        />

        {/* Close Button */}
        <button
          onClick={() => {
            soundEffects.playTap();
            onCancel();
          }}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/5 transition"
          aria-label="إغلاق"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header Icon */}
        <div className="flex justify-center mb-4 mt-2">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center ring-4 ${styleMeta.ringColor} ${styleMeta.bgIcon} shadow-xl transform transition-transform duration-300 hover:scale-105`}
          >
            {styleMeta.iconNode}
          </div>
        </div>

        {/* Content */}
        <h3 className="text-lg font-black text-white mb-2 leading-snug">{title}</h3>
        <p className="text-xs text-slate-300 mb-6 leading-relaxed px-2">{description}</p>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              soundEffects.playTap();
              onCancel();
            }}
            disabled={isLoading}
            className="flex-1 py-2.5 px-4 rounded-xl bg-[#141c2c] hover:bg-[#1a2438] active:scale-95 border border-slate-700/80 text-slate-200 font-bold text-xs transition"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={() => {
              soundEffects.playSent();
              onConfirm();
            }}
            disabled={isLoading}
            className={`flex-1 py-2.5 px-4 rounded-xl font-bold text-xs active:scale-95 transition flex items-center justify-center gap-1.5 ${styleMeta.btnClass} ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              resolvedConfirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
